import { Stack, IconButton, Box, Typography, Divider, Chip } from "@mui/material";
import { Tabs, Tab } from "@mui/material";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import React, { useRef, useState, useEffect } from "react";
import { ApiEndpoint } from "../../types";

interface EndpointButtonsProps {
  endpoints: ApiEndpoint[];
  currentEndpoint: ApiEndpoint | null;
  onEndpointChange: (endpoint: ApiEndpoint) => void;
  disabled?: boolean;
  isLoading?: boolean;
}

interface EndpointGroup {
  label: string;
  endpoints: ApiEndpoint[];
  color: string;
}

const EndpointButtons: React.FC<EndpointButtonsProps> = ({
  endpoints,
  currentEndpoint,
  onEndpointChange,
  disabled = false,
  isLoading = false,
}) => {
  // Group endpoints by type
  const endpointGroups: EndpointGroup[] = React.useMemo(() => {
    // Get all unique data source types
    const dataSourceTypes = [...new Set(endpoints.map(ep => ep.dataSourceType || 'base'))];
    
    // Create groups dynamically using the actual dataSourceType as label
    const groups: EndpointGroup[] = dataSourceTypes.map(type => {
      const typeEndpoints = endpoints.filter(ep => (ep.dataSourceType || 'base') === type);
      const displayLabel = type === 'base' ? 'BASE DATA' : type.toUpperCase();
      
      return {
        label: displayLabel,
        endpoints: typeEndpoints,
        color: '#4A73AB' // Consistent color for all groups
      };
    });
    
    return groups;
  }, [endpoints]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    const selectedEndpoint = endpoints.find(ep => ep.tableName === newValue);
    if (selectedEndpoint) {
      onEndpointChange(selectedEndpoint);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {endpointGroups.map((group, groupIndex) => (
        <EndpointGroupRow
          key={group.label}
          group={group}
          currentEndpoint={currentEndpoint}
          onEndpointChange={handleChange}
          disabled={disabled}
          isLoading={isLoading}
          isLast={groupIndex === endpointGroups.length - 1}
          showGroupLabel={endpointGroups.length > 1}
        />
      ))}
    </Box>
  );
};

// Separate component for each endpoint group row
interface EndpointGroupRowProps {
  group: EndpointGroup;
  currentEndpoint: ApiEndpoint | null;
  onEndpointChange: (event: React.SyntheticEvent, newValue: string) => void;
  disabled: boolean;
  isLoading: boolean;
  isLast: boolean;
  showGroupLabel: boolean;
}

const EndpointGroupRow: React.FC<EndpointGroupRowProps> = ({
  group,
  currentEndpoint,
  onEndpointChange,
  disabled,
  isLoading,
  isLast,
  showGroupLabel
}) => {
  const tabsRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check scroll position and update button states
  const checkScrollPosition = () => {
    if (tabsRef.current) {
      const tabsElement = tabsRef.current.querySelector('.MuiTabs-scroller');
      if (tabsElement) {
        const { scrollLeft, scrollWidth, clientWidth } = tabsElement;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
      }
    }
  };

  // Check scroll position on mount and when endpoints change
  useEffect(() => {
    checkScrollPosition();
    
    const tabsElement = tabsRef.current?.querySelector('.MuiTabs-scroller');
    if (tabsElement) {
      tabsElement.addEventListener('scroll', checkScrollPosition);
      return () => tabsElement.removeEventListener('scroll', checkScrollPosition);
    }
  }, [group.endpoints]);

  // Scroll left function
  const scrollLeft = () => {
    const tabsElement = tabsRef.current?.querySelector('.MuiTabs-scroller');
    if (tabsElement) {
      tabsElement.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  // Scroll right function
  const scrollRight = () => {
    const tabsElement = tabsRef.current?.querySelector('.MuiTabs-scroller');
    if (tabsElement) {
      tabsElement.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // Check if current endpoint is in this group
  const currentValue = group.endpoints.find(ep => ep.tableName === currentEndpoint?.tableName) 
    ? currentEndpoint?.tableName 
    : false;

  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      width: '100%',
      mb: isLast ? 0 : 0.5,
      minHeight: '48px'
    }}>
      {/* Group label on the left */}
      {showGroupLabel && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          minWidth: '100px',
          maxWidth: '100px',
          mr: 1,
          height: '48px',
          flexShrink: 0,
          background: `linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.03))`,
          borderLeft: `3px solid rgba(255, 255, 255, 0.2)`,
          borderRadius: '0 8px 8px 0',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '1px',
            background: `linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.3), transparent)`,
          }
        }}>
          <Chip
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    lineHeight: 1,
                    flex: 1,
                  }}
                >
                  {group.label}
                </Typography>
              </Box>
            }
            sx={{
              width: '100%',
              height: '100%',
              justifyContent: 'flex-start',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: `1px solid rgba(255, 255, 255, 0.1)`,
              borderRadius: '0 6px 6px 0',
              '& .MuiChip-label': {
                width: '100%',
                padding: '0 8px',
                display: 'flex',
                justifyContent: 'flex-start',
              },
            }}
          />
        </Box>
      )}

      {/* Left scroll button */}
      <IconButton
        onClick={scrollLeft}
        disabled={!canScrollLeft || disabled || isLoading}
        size="small"
        sx={{ 
          color: 'rgba(255, 255, 255, 0.7)',
          opacity: canScrollLeft ? 1 : 0.3,
          minWidth: 'auto',
          mr: 0.5,
          flexShrink: 0,
          '&:hover': {
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <ArrowBackIosIcon fontSize="small" />
      </IconButton>

      {/* Tabs container */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Tabs
          ref={tabsRef}
          value={currentValue}
          onChange={onEndpointChange}
          variant="scrollable"
          scrollButtons={false}
          allowScrollButtonsMobile={false}
          indicatorColor="secondary"
          textColor="inherit"
          aria-label={`${group.label} endpoint tabs`}
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            minHeight: '42px',
            '& .MuiTabs-indicator': {
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
            },
            '& .MuiTab-root': {
              minWidth: '100px',
              fontSize: '0.8rem',
              color: 'rgba(255, 255, 255, 0.7)',
              opacity: (disabled || isLoading) ? 0.5 : 1,
              minHeight: '42px',
              padding: '6px 12px',
              transition: 'all 0.2s ease-in-out',
              '&.Mui-selected': {
                color: 'white',
              },
              '&.Mui-disabled': {
                opacity: 0.5,
                color: 'rgba(255, 255, 255, 0.5)',
              },
              '&:hover:not(.Mui-selected):not(.Mui-disabled)': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px rgba(255, 255, 255, 0.1)`,
                color: 'rgba(255, 255, 255, 0.9)',
              },
            }
          }}
        >
          {group.endpoints.map((ep) => (
            <Tab
              key={ep.tableName}
              label={ep.name}
              value={ep.tableName}
              wrapped
            />
          ))}
        </Tabs>
      </Box>

      {/* Right scroll button */}
      <IconButton
        onClick={scrollRight}
        disabled={!canScrollRight || disabled || isLoading}
        size="small"
        sx={{ 
          color: 'rgba(255, 255, 255, 0.7)',
          opacity: canScrollRight ? 1 : 0.3,
          minWidth: 'auto',
          ml: 0.5,
          flexShrink: 0,
          '&:hover': {
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <ArrowForwardIosIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default React.memo(EndpointButtons); 